/**
 * Custom hook for Plaid Link integration
 * Handles link token fetching, token exchange, and Plaid Link callbacks
 *
 * WP-FE-7: Refactored to use React Query for token caching and deduplication.
 * This prevents duplicate /link/token/create API calls when:
 * - Component remounts (parent re-render, route change)
 * - React StrictMode double-invokes effects
 * - Multiple PlaidLinkButton instances on page
 */

import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import {
  usePlaidLink as usePlaidLinkBase,
  PlaidLinkOnSuccess,
  PlaidLinkOnExit,
  PlaidLinkOnEvent,
  PlaidLinkOptions,
} from 'react-plaid-link';
import { connectionsApi } from '@/services/connections';
import { connectionKeys } from '@/hooks/useConnections';
import type { ExchangeTokenResponse, PlaidAccount } from '@/types';

export interface UsePlaidLinkOptions {
  /**
   * Connection ID to update (for re-authentication flows)
   */
  connectionId?: string;
  /**
   * @deprecated WP-CONN-1: Products are now handled by the backend.
   * Backend always requests transactions + investments consent.
   * This prop is kept for backward compatibility but is ignored.
   */
  products?: string[];
  /**
   * Callback when link flow completes successfully
   */
  onSuccess?: (response: ExchangeTokenResponse) => void;
  /**
   * Callback when user exits link flow (cancelled or error)
   */
  onExit?: (error: PlaidLinkError | null) => void;
  /**
   * Callback for Plaid Link events (for analytics)
   */
  onEvent?: PlaidLinkOnEvent;
}

export interface PlaidLinkError {
  errorCode: string;
  errorMessage: string;
  displayMessage: string | null;
}

export interface UsePlaidLinkReturn {
  /**
   * Open Plaid Link modal
   */
  open: () => void;
  /**
   * Whether Plaid Link is ready to open
   */
  ready: boolean;
  /**
   * Whether link token is being fetched
   */
  isLoadingToken: boolean;
  /**
   * Whether token exchange is in progress
   */
  isExchanging: boolean;
  /**
   * Error from token fetching or exchange
   */
  error: string | null;
  /**
   * Clear error state
   */
  clearError: () => void;
  /**
   * Refresh the link token
   */
  refreshToken: () => Promise<void>;
}

/**
 * Custom hook for Plaid Link integration
 *
 * @example
 * ```tsx
 * function AddBankButton() {
 *   const { open, ready, isLoadingToken, error } = usePlaidLink({
 *     onSuccess: (response) => {
 *       // Handle successful connection
 *     },
 *   });
 *
 *   return (
 *     <Button onClick={open} disabled={!ready || isLoadingToken}>
 *       {isLoadingToken ? 'Loading...' : 'Add Bank Account'}
 *     </Button>
 *   );
 * }
 * ```
 */
export function usePlaidLink(options: UsePlaidLinkOptions = {}): UsePlaidLinkReturn {
  // WP-CONN-1: products is deprecated - the backend picks them. The leading
  // underscore is what satisfies no-unused-vars; the disable comment that used
  // to be here did nothing and was itself reported.
  const { connectionId, products: _products, onSuccess, onExit, onEvent } = options;

  const [isExchanging, setIsExchanging] = useState(false);
  const [exchangeError, setExchangeError] = useState<string | null>(null);

  /**
   * WP-FE-7: Fetch link token with React Query caching
   * WP-CONN-1: Backend now handles product selection (transactions + investments consent)
   *
   * Benefits:
   * - Deduplicates concurrent requests with same key
   * - Caches for 4 minutes (tokens expire in 5)
   * - Prevents re-fetch on component remount within staleTime
   * - React StrictMode safe (double-invoke uses same cached promise)
   */
  const {
    data: linkToken,
    isLoading: isLoadingToken,
    error: tokenQueryError,
    refetch,
  } = useQuery({
    // WP-CONN-1: No products in cache key - backend uses fixed products
    queryKey: connectionKeys.linkToken(connectionId ?? null),
    queryFn: async () => {
      const response = connectionId
        ? await connectionsApi.getRelinkToken(connectionId)
        : await connectionsApi.createPlaidLinkToken();  // WP-CONN-1: No products - backend handles
      return response.linkToken;
    },
    staleTime: 4 * 60 * 1000, // 4 minutes (tokens expire in 5)
    gcTime: 5 * 60 * 1000, // 5 minutes cache before garbage collection
    retry: 1, // Only retry once on failure
    refetchOnWindowFocus: false, // Don't refetch when tab gains focus
    refetchOnReconnect: false, // Don't refetch on network reconnect
  });

  // Combine token fetch error with exchange error for unified error state
  const tokenError = tokenQueryError
    ? (tokenQueryError as Error).message || 'Failed to initialize bank connection'
    : null;
  const error = exchangeError || tokenError;

  /**
   * Handle successful Plaid Link completion
   */
  const handleSuccess: PlaidLinkOnSuccess = useCallback(
    async (publicToken, metadata) => {
      setIsExchanging(true);
      setExchangeError(null);

      try {
        // Map Plaid accounts to our format
        const accounts: PlaidAccount[] = metadata.accounts.map((acc) => ({
          id: acc.id,
          name: acc.name,
          mask: acc.mask,
          type: acc.type,
          subtype: acc.subtype,
        }));

        // Exchange public token for access token
        const response = await connectionsApi.exchangePlaidToken({
          publicToken,
          institutionId: metadata.institution?.institution_id || '',
          institutionName: metadata.institution?.name || 'Unknown Institution',
          accounts,
        });

        onSuccess?.(response);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to complete bank connection';
        setExchangeError(errorMessage);
        // Log error for debugging (no PII)
        if (import.meta.env.DEV) {
          console.error('Failed to exchange token:', errorMessage);
        }
        // Send to Sentry in production (no PII)
        Sentry.captureMessage('Failed to exchange Plaid token', {
          level: 'error',
          extra: { errorMessage },
        });
      } finally {
        setIsExchanging(false);
      }
    },
    [onSuccess]
  );

  /**
   * Handle Plaid Link exit (user cancelled or error)
   */
  const handleExit: PlaidLinkOnExit = useCallback(
    (plaidError, metadata) => {
      // Map Plaid error to our format
      const exitError: PlaidLinkError | null = plaidError
        ? {
            errorCode: plaidError.error_code,
            errorMessage: plaidError.error_message,
            displayMessage: plaidError.display_message,
          }
        : null;

      // Only log error codes, not sensitive details
      if (import.meta.env.DEV && exitError) {
        console.error('Plaid Link exit with error:', exitError.errorCode);
      }
      // Send to Sentry in production (only error code, no PII)
      if (exitError) {
        Sentry.captureMessage('Plaid Link exit with error', {
          level: 'warning',
          extra: { errorCode: exitError.errorCode },
        });
      }

      // Set user-friendly error message for display
      if (exitError && exitError.displayMessage) {
        setExchangeError(exitError.displayMessage);
      } else if (exitError) {
        // Generic error for non-displayable errors
        setExchangeError('Unable to connect to your bank. Please try again.');
      }

      // Track exit event details (link session ID for debugging)
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.debug('Plaid Link exit:', {
          linkSessionId: metadata.link_session_id,
          exitStatus: metadata.status,
          hasError: !!exitError,
        });
      }

      onExit?.(exitError);
    },
    [onExit]
  );

  /**
   * Handle Plaid Link events for analytics
   */
  const handleEvent: PlaidLinkOnEvent = useCallback(
    (eventName, metadata) => {
      // Log event for debugging (no PII - only event names)
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.debug('Plaid Link event:', eventName);
      }

      onEvent?.(eventName, metadata);
    },
    [onEvent]
  );

  /**
   * Configure Plaid Link
   */
  const config: PlaidLinkOptions = {
    token: linkToken ?? null,
    onSuccess: handleSuccess,
    onExit: handleExit,
    onEvent: handleEvent,
  };

  const { open, ready } = usePlaidLinkBase(config);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setExchangeError(null);
  }, []);

  /**
   * Refresh the link token (useful if expired)
   * Uses React Query's refetch to get a fresh token
   */
  const refreshToken = useCallback(async () => {
    await refetch();
  }, [refetch]);

  return {
    open: open as () => void,
    ready: ready && !isExchanging && !!linkToken,
    isLoadingToken,
    isExchanging,
    error,
    clearError,
    refreshToken,
  };
}

export default usePlaidLink;
